import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { Box } from "@mui/material";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import {
  CreatePlaceholderTaskPayload,
  useDespatchActions,
} from "../../../Diary/views/DiaryResourcesView/hooks/useDespatchActions";
import { Color } from "Components/Form/Button";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useHandleValidate } from "Hooks/useYupValidation";
import { PlaceholderTask, Task, TaskStartDatePeriod } from "Models/task";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";
import { getMuiPopupContainer } from "Themes/dispatchTheme";
import { TaskStatus } from "Util/enums";
import * as yup from "yup";

import { useCreateTaskForm } from "../../../../hooks/useCreateTaskForm";
import { useStyles, StyledModal } from "./styles";
import { zonedTimeToUtc } from "date-fns-tz";
import { TimeSlotTask } from "Models/time.slot.task";
import { formatSimpleDate } from "Util/formatDateAndTime";
import { useAppState } from "Context/StateContext";

type TeamListOption = Pick<Team, "id" | "name">;

type Props = {
  onSuccessTaskCreated?: (response: Task, variables: CreatePlaceholderTaskPayload) => void;
};
export type DespatchCreatePlaceholderTaskModalHandles = {
  open: (teamList: TeamListOption[]) => void;
  close: () => void;
};

const DespatchCreatePlaceholderTaskComponent: ForwardRefRenderFunction<
  DespatchCreatePlaceholderTaskModalHandles,
  Props
> = (props, ref) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { currentDate, despatchGlobalFilters } = useOperationDespatchContext();
  const { formatWithUserTimezone, userTimezone } = useAppState();

  const { useCreatePlaceholderTask, getCompaniesWithConnection } = useDespatchActions();
  const { data: companies, isLoading: isLoadingCompanies } = getCompaniesWithConnection();

  const { mutate: createPlaceholderTask, isLoading: isLoadingTasks } = useCreatePlaceholderTask(
    (response, variables) => props.onSuccessTaskCreated?.(response, variables)
  );

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const [teamList, setTeamList] = useState<TeamListOption[]>([]);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isLoading = useMemo(() => isLoadingTasks, [isLoadingTasks]);
  const defaultCompanyId = useMemo(() => {
    if (despatchGlobalFilters?.companyIds?.length === 1) {
      return despatchGlobalFilters?.companyIds[0];
    }

    return undefined;
  }, [despatchGlobalFilters?.companyIds]);

  const defaultCompany = useMemo(
    () => companies?.find((company) => company.id === defaultCompanyId),
    [companies, defaultCompanyId]
  );

  const {
    form,
    task,
    companySelected,
    description,
    isMultiDayTask,
    setTaskTypeSelected,
    handleChangePeriod,
    clearAllFields,
  } = useCreateTaskForm({
    errors,
    isLoading,
    companies,
    isLoadingCompanies,
    showDescription: true,
    defaultCompany,
    isPlaceholderTask: true,
    currentDate,
  });

  const schema = yup.object().shape({
    isMultiDayTask: yup.boolean().nullable(),
    companyId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    taskName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    timeSlots: yup.array().when("isMultiDayTask", {
      is: true,
      then: yup.array().when("period", {
        is: TaskStartDatePeriod.VARIABLE_TIME,
        then: yup
          .array()
          .min(2, translate("itShouldBeAtLeastTwoDates"))
          .of(
            yup.object().shape({
              date: yup
                .string()
                .required(translate("thisFieldIsRequired"))
                .typeError(translate("thisFieldIsRequired")),
              from: yup
                .string()
                .required(translate("thisFieldIsRequired"))
                .typeError(translate("thisFieldIsRequired")),
              to: yup
                .string()
                .required(translate("thisFieldIsRequired"))
                .test(
                  "end-time-greater-than-start-time",
                  translate("endTimeMustBeGreaterThanStartTime"),
                  function (value) {
                    const { date, from } = this.parent;
                    if (!from || !value) return true;

                    const currentDate = date.split("T")[0];
                    const startTime = new Date(`${currentDate}T${from}`);
                    const endTime = new Date(`${currentDate}T${value}`);

                    return endTime > startTime;
                  }
                ),
            })
          )
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.array().nullable(),
      }),
      otherwise: yup.array().nullable(),
    }),
    startDate: yup.date().when("timeSlots", {
      is: (value: TimeSlotTask[]) => value?.length === 0,
      then: yup
        .date()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.date().nullable(),
    }),
    endDate: yup.date().when("isMultiDayTask", {
      is: true,
      then: yup.date().when("timeSlots", {
        is: (value: TimeSlotTask[]) => value?.length === 0,
        then: yup
          .date()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        otherwise: yup.date().nullable(),
      }),
      otherwise: yup.date().nullable(),
    }),
    startTime: yup.string().when("timeSlots", {
      is: (value: TimeSlotTask[]) => value?.length === 0,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    endTime: yup.string().when("timeSlots", {
      is: (value: TimeSlotTask[]) => value?.length === 0,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .test(
          "end-time-greater-than-start-time",
          translate("endTimeMustBeGreaterThanStartTime"),
          function (value) {
            const { startDate, startTime: from } = this.parent;
            if (!from || !value) return true;

            const currentDate = formatWithUserTimezone(new Date(startDate), "yyyy-MM-dd");
            const startTime = new Date(`${currentDate}T${from}`);
            const endTime = new Date(`${currentDate}T${value}`);

            return endTime > startTime;
          }
        )
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
  });

  const isValidated = () => {
    const validated = useHandleValidate(
      {
        companyId: companySelected?.id,
        isMultiDayTask,
        ...task,
      },
      schema,
      setErrors
    );

    return validated;
  };

  const getStartAndEndDate = () => {
    if (!task.timeSlots?.length) {
      return { startDate: task.startDate, endDate: task.endDate };
    }

    const currentTimeSlots = task.timeSlots.sort(
      (a, b) =>
        zonedTimeToUtc(a.date!, userTimezone.name).getTime() -
        zonedTimeToUtc(b.date!, userTimezone.name).getTime()
    );

    const startCurrentDate = formatSimpleDate(new Date(currentTimeSlots?.[0]?.date as string), {
      formatString: "yyyy-MM-dd",
      isUTC: true,
    });

    const indexOfLastDate = currentTimeSlots?.length - 1;
    const endCurrentDate = formatSimpleDate(
      new Date(currentTimeSlots?.[indexOfLastDate]?.date as string),
      { formatString: "yyyy-MM-dd", isUTC: true }
    );

    const startCurrentTime = currentTimeSlots?.[0]?.from;
    const currentStartDate = new Date(`${startCurrentDate}T${startCurrentTime}`);

    const startDate = zonedTimeToUtc(currentStartDate, userTimezone.name).toISOString();

    const endCurrentTime = currentTimeSlots?.[indexOfLastDate]?.from;
    const currentEndDate = new Date(`${endCurrentDate}T${endCurrentTime}`);

    const endDate = zonedTimeToUtc(currentEndDate, userTimezone.name).toISOString();

    return {
      startDate,
      endDate,
    };
  };

  const onSubmit = () => {
    setIsSubmitted(true);
    if (!task.startDate) {
      NotificationToast.warning(translate("pleaseSelectAStartDate"), {
        id: "pleaseSelectAStartDate",
      });

      return;
    }

    if (!isValidated()) return;

    const { startDate, endDate } = getStartAndEndDate();

    createPlaceholderTask(
      {
        data: {
          companyId: companySelected?.id,
          name: task.taskName,
          period: task.period,
          startDate,
          endDate,
          timeSlots: task.timeSlots,
          status: TaskStatus.REGISTERED,
          description,
        } as PlaceholderTask,
      },
      {
        onSuccess: () => {
          onCloseModal();
        },
      }
    );
  };

  const [isOpen, setIsOpen] = useState(false);

  const onOpenModal = useCallback(
    (_teams: TeamListOption[]) => {
      setTeamList(_teams || []);
      handleChangePeriod({ period: TaskStartDatePeriod.MORNING, currentDate });
      setIsOpen(true);
    },
    [currentDate]
  );

  const onCloseModal = useCallback(() => {
    clearAllFields();
    setIsOpen(false);
    setTaskTypeSelected("");
    setErrors([]);
    setIsSubmitted(false);
  }, [defaultCompany]);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <StyledModal
      container={getMuiPopupContainer}
      title={translate("newPlaceholderTask")}
      description={translate("createAnInternalTaskOrARegisterForADraftTask")}
      open={isOpen}
      handleClose={onCloseModal}
      className={classes.modal}
      data-cy="modalNewPlaceholderTask"
      dataCyCloseButton="modalNewTaskCloseButton"
      showActionButtons
      modalFooterCustomClass={classes.modalFooter}
      actionButtonProps={{
        textSave: translate("create"),
        customColorCancel: Color.TEXT_INFO,
        saveDisabled: isLoading,
        cancelDisabled: false,
        loading: isLoading,
        onCancel: () => onCloseModal(),
        onSave: onSubmit,
      }}
    >
      <Box>
        <Grid container spacing={2}>
          {form}
        </Grid>
      </Box>
    </StyledModal>
  );
};

const DespatchCreatePlaceholderTask = forwardRef(DespatchCreatePlaceholderTaskComponent);

export { DespatchCreatePlaceholderTask };
